import { createSelector } from "@reduxjs/toolkit";
import _ from "underscore";

import { LOAD_COMPLETE_FAVICON } from "metabase/common/hooks/constants";
import { canManageSubscriptions, getUserIsAdmin } from "metabase/current-user";
import {
  DASHBOARD_SLOW_TIMEOUT,
  SIDEBAR_NAME,
} from "metabase/dashboard/constants";
import { isEmbeddingSdk } from "metabase/embedding-sdk/config";
import type { SdkSharedStoreState } from "metabase/embedding-sdk/types/store";
import { getEmbedOptions } from "metabase/embedding/interactive-embedding";
import { getIsWebApp } from "metabase/embedding/selectors";
import {
  getShallowDatabases,
  selectQuestionFromCardBuilder,
} from "metabase/metadata-store";
import {
  getDashboardQuestions,
  getSavedDashboardUiParameters,
  getUnsavedDashboardUiParameters,
} from "metabase/parameters/utils/dashboards";
import { getParameterMappingOptions as _getParameterMappingOptions } from "metabase/parameters/utils/mapping-options";
import { getVisibleParameters } from "metabase/parameters/utils/ui";
import type {
  ClickBehaviorSidebarState,
  EditParameterSidebarState,
  EventsSidebarState,
  SharingSidebarState,
  State,
  StoreDashboard,
  StoreDashcard,
} from "metabase/redux/store";
import { getSetting } from "metabase/settings";
import * as Urls from "metabase/urls";
import { isQuestionCard, isQuestionDashCard } from "metabase/utils/dashboard";
import { getPathnameWithoutSubPath } from "metabase/utils/dom";
import { selectIsWithinIframe } from "metabase/utils/iframe";
import { isNotNull } from "metabase/utils/types";
import { extendCardWithDashcardSettings } from "metabase/viz-core";
import {
  getValuePopulatedParameters as _getValuePopulatedParameters,
  getParameterValuesBySlug,
} from "metabase-lib/v1/parameters/utils/parameter-values";
import type {
  Card,
  ChannelApiResponse,
  DashCardId,
  Dashboard,
  DashboardCard,
  DashboardId,
  DashboardParameterMapping,
  DashboardTabId,
  EmbeddingParameterVisibility,
  Parameter,
  ParameterId,
  VirtualCard,
} from "metabase-types/api";

import { getNewCardUrl } from "./actions/getNewCardUrl";
import {
  getDashboard,
  getDashboardBeforeEditing,
  getDashboardId,
  getDashboards,
  getIsEditing,
} from "./shell-selectors";
import {
  canResetFilter,
  findDashCardForInlineParameter,
  getMappedParametersIds,
  hasDatabaseActionsEnabled,
  hasInlineParameters,
  isDashcardInlineParameter,
} from "./utils";

type SidebarState = State["dashboard"]["sidebar"];

function isClickBehaviorSidebar(
  sidebar: SidebarState,
): sidebar is ClickBehaviorSidebarState {
  return sidebar.name === SIDEBAR_NAME.clickBehavior;
}

function isEventsSidebar(sidebar: SidebarState): sidebar is EventsSidebarState {
  return sidebar.name === SIDEBAR_NAME.events;
}

export const getEventsSidebarProps = (state: State) => {
  const { sidebar } = state.dashboard;
  return isEventsSidebar(sidebar) ? sidebar.props : null;
};

function isSharingSidebar(
  sidebar: SidebarState,
): sidebar is SharingSidebarState {
  return sidebar.name === SIDEBAR_NAME.sharing;
}

export const getSharingSubscriptionId = (state: State) => {
  const { sidebar } = state.dashboard;
  return isSharingSidebar(sidebar) ? sidebar.props.subscriptionId : undefined;
};

function isEditParameterSidebar(
  sidebar: SidebarState,
): sidebar is EditParameterSidebarState {
  return sidebar.name === SIDEBAR_NAME.editParameter;
}

export const getClickBehaviorSidebarDashcard = (state: State) => {
  const { sidebar, dashcards } = state.dashboard;
  return isClickBehaviorSidebar(sidebar)
    ? dashcards[sidebar.props?.dashcardId]
    : null;
};
export const getDashcardDataMap = (state: State) =>
  state.dashboard.dashcardData;

export const getDashcardData = createSelector(
  [getDashcardDataMap, (_state: State, dashcardId: DashCardId) => dashcardId],
  (dashcardDataMap, dashcardId) => {
    return dashcardDataMap[dashcardId];
  },
);

export const getSlowCards = (state: State) => state.dashboard.slowCards;
export const getParameterValues = (state: State) =>
  state.dashboard.parameterValues;
export const getFavicon = (state: State) =>
  state.dashboard.loadingControls?.showLoadCompleteFavicon
    ? LOAD_COMPLETE_FAVICON
    : null;

export const getIsDashCardsRunning = (state: State) =>
  state.dashboard.loadingDashCards.loadingStatus === "running";
export const getIsDashCardsLoadingComplete = (state: State) =>
  state.dashboard.loadingDashCards.loadingStatus === "complete";

export const getLoadingStartTime = (state: State) =>
  state.dashboard.loadingDashCards.startTime;
export const getLoadingEndTime = (state: State) =>
  state.dashboard.loadingDashCards.endTime;

export const getIsSlowDashboard = createSelector(
  [getLoadingStartTime, getLoadingEndTime],
  (startTime, endTime) => {
    if (startTime != null && endTime != null) {
      return endTime - startTime > DASHBOARD_SLOW_TIMEOUT;
    } else {
      return false;
    }
  },
);

export const getIsLoadingWithoutCards = (state: State) =>
  state.dashboard.loadingControls.isLoading;

export const getIsLoading = (state: State) =>
  getIsLoadingWithoutCards(state) || !getIsDashCardsLoadingComplete(state);

export const getIsAddParameterPopoverOpen = (state: State) =>
  state.dashboard.isAddParameterPopoverOpen;

export const getSidebar = (state: State) => state.dashboard.sidebar;
export const getIsSidebarOpen = createSelector(
  [getSidebar],
  (sidebar) => !!sidebar.name,
);
export const getIsSharing = createSelector(
  [getSidebar],
  (sidebar) => sidebar.name === SIDEBAR_NAME.sharing,
);

/**
 * Whether the current user can set up and edit this dashboard's
 * subscriptions. It takes the Subscriptions permission and a card with data
 * to send, and for a non-admin a configured email or Slack channel, since
 * only an admin can set one up. `channelInfo` is `undefined` while it loads
 * or when its request fails, which leaves only an admin able to manage them.
 */
export const getCanManageDashboardSubscriptions = (
  state: State,
  dashboard: Pick<Dashboard, "dashcards">,
  channelInfo: ChannelApiResponse | undefined,
): boolean => {
  const hasDataCards = dashboard.dashcards.some(
    (dashcard) => !["text", "heading"].includes(dashcard.card.display),
  );
  const hasConfiguredChannel =
    channelInfo?.channels.email?.configured === true ||
    channelInfo?.channels.slack?.configured === true;

  return (
    canManageSubscriptions(state) &&
    hasDataCards &&
    (getUserIsAdmin(state) || hasConfiguredChannel)
  );
};

export const getShowAddQuestionSidebar = createSelector(
  [getSidebar],
  (sidebar) => sidebar.name === SIDEBAR_NAME.addQuestion,
);

export const getIsShowDashboardInfoSidebar = createSelector(
  [getSidebar],
  (sidebar) => sidebar.name === SIDEBAR_NAME.info,
);

export const getIsShowDashboardSettingsSidebar = createSelector(
  [getSidebar],
  (sidebar) => sidebar.name === SIDEBAR_NAME.settings,
);

export const getDashcards = (state: State) => state.dashboard.dashcards;

export const getDashCardById = (state: State, dashcardId: DashCardId) => {
  const dashcards = getDashcards(state);
  return dashcards[dashcardId];
};

export function getDashCardBeforeEditing(state: State, dashcardId: DashCardId) {
  const dashboard = getDashboardBeforeEditing(state);
  return dashboard?.dashcards?.find?.((dashcard) => dashcard.id === dashcardId);
}

export const getLoadingDashCards = (state: State) =>
  state.dashboard.loadingDashCards;

export const getDashboardById = (state: State, dashboardId: DashboardId) => {
  const dashboards = getDashboards(state);
  return dashboards[dashboardId];
};

const EMPTY_DASHCARDS: StoreDashcard[] = [];
const EMPTY_PARAMETERS: Parameter[] = [];

/**
 * The dashboard as we last saw it, looked up by id in the in-memory Redux
 * cache. Unlike `getDashboard`, it survives the reset of the active
 * `dashboardId` while a dashboard (re)loads, so a dashboard seen earlier in
 * this session can draw its real layout as a skeleton. Undefined on any fresh
 * page load.
 */
export const getLastSeenDashboard = (
  state: State,
  dashboardId: DashboardId | null,
): StoreDashboard | undefined =>
  dashboardId == null ? undefined : getDashboardById(state, dashboardId);

/**
 * Whether the dashboard as last seen is fixed width. Without it in the cache,
 * assume it is, since fixed is the default width for dashboards.
 */
export const getIsLastSeenDashboardFixedWidth = (
  state: State,
  dashboardId: DashboardId | null,
): boolean =>
  (getLastSeenDashboard(state, dashboardId)?.width ?? "fixed") === "fixed";

/**
 * The dashcards of the tab the user is landing on, in the dashboard as last
 * seen. The tab is chosen the way `getSelectedTabId` chooses it on load.
 */
export const getLastSeenTabDashcards = createSelector(
  [
    getLastSeenDashboard,
    getDashcards,
    getIsWebApp,
    (state: State) => getSetting(state, "site-url"),
    (state: State & Partial<SdkSharedStoreState>) =>
      state.sdk?.initialDashboardTabId,
  ],
  (
    dashboard,
    dashcardMap,
    isWebApp,
    siteUrl,
    sdkInitialDashboardTabId,
  ): StoreDashcard[] => {
    if (!dashboard) {
      return EMPTY_DASHCARDS;
    }

    const dashboardWithVisibleTabs = {
      ...dashboard,
      tabs: dashboard.tabs?.filter((tab) => !tab.isRemoved),
    };
    const tabId = isEmbeddingSdk()
      ? getSdkInitialDashboardTabId(
          dashboardWithVisibleTabs,
          sdkInitialDashboardTabId,
        )
      : getInitialSelectedTabId(dashboardWithVisibleTabs, siteUrl, isWebApp);

    const dashcards = dashboard.dashcards
      .map((id) => dashcardMap[id])
      .filter((dc): dc is StoreDashcard => isNotNull(dc) && !dc.isRemoved);
    return tabId == null
      ? dashcards
      : dashcards.filter((dc) => dc.dashboard_tab_id === tabId);
  },
);

/**
 * The filters shown in the header of the dashboard as last seen: its
 * parameters except those placed inline on a dashcard.
 */
export const getLastSeenDashboardHeaderParameters = createSelector(
  [getLastSeenDashboard, getDashcards],
  (dashboard, dashcardMap): Parameter[] => {
    if (!dashboard?.parameters) {
      return EMPTY_PARAMETERS;
    }

    const dashcards = dashboard.dashcards
      .map((id) => dashcardMap[id])
      .filter(isNotNull);
    return dashboard.parameters.filter(
      (parameter) => !isDashcardInlineParameter(parameter.id, dashcards),
    );
  },
);

export const getLinkTargetEntities = (state: State) =>
  state.dashboard.linkTargets;

export const getDashboardComplete = createSelector(
  [getDashboard, getDashcards],
  (dashboard, dashcards) => {
    if (!dashboard) {
      return null;
    }

    const orderedDashcards = dashboard.dashcards
      .map((id) => dashcards[id])
      .filter((dc) => !dc.isRemoved)
      .sort((a, b) => {
        const rowDiff = a.row - b.row;

        // sort by y position first
        if (rowDiff !== 0) {
          return rowDiff;
        }

        // for items on the same row, sort by x position
        return a.col - b.col;
      });

    return (
      dashboard && {
        ...dashboard,
        dashcards: orderedDashcards,
      }
    );
  },
);

export const getCurrentDashcards = createSelector(
  [getDashboardComplete],
  (dashboard) => dashboard?.dashcards || [],
);

export const getDashcardHref = createSelector(
  [
    selectQuestionFromCardBuilder,
    getDashboardComplete,
    getParameterValues,
    getDashCardById,
  ],
  (buildQuestion, dashboard, parameterValues, dashcard) => {
    if (
      !dashboard ||
      !dashcard ||
      !isQuestionDashCard(dashcard) ||
      !dashcard.card.dataset_query // cards without queries will cause Lib to throw in getNewCardUrl
    ) {
      return undefined;
    }

    const card = extendCardWithDashcardSettings(
      dashcard.card,
      dashcard.visualization_settings,
    );

    return getNewCardUrl({
      buildQuestion,
      dashboard,
      parameterValues,
      dashcard,
      nextCard: card,
      previousCard: card,
    });
  },
);

export const getAutoApplyFiltersToastId = (state: State) =>
  state.dashboard.autoApplyFilters.toastId;
export const getAutoApplyFiltersToastDashboardId = (state: State) =>
  state.dashboard.autoApplyFilters.toastDashboardId;
export const getDraftParameterValues = (state: State) =>
  state.dashboard.draftParameterValues;

export const getIsAutoApplyFilters = createSelector(
  [getDashboard],
  (dashboard) => !!dashboard?.auto_apply_filters,
);
export const getHasUnappliedParameterValues = createSelector(
  [getParameterValues, getDraftParameterValues],
  (parameterValues, draftParameterValues) => {
    return !_.isEqual(draftParameterValues, parameterValues);
  },
);

export const getEffectiveParameterValues = createSelector(
  [getParameterValues, getDraftParameterValues, getIsAutoApplyFilters],
  (values, draftValues, isAutoApplyFilters) =>
    isAutoApplyFilters ? values : draftValues,
);

const getIsParameterValuesEmpty = createSelector(
  [getParameterValues],
  (parameterValues) => {
    return Object.values(parameterValues).every((parameterValue) =>
      Array.isArray(parameterValue)
        ? parameterValue.length === 0
        : parameterValue == null,
    );
  },
);

export const getParameterValuesBySlugMap = createSelector(
  [getDashboardComplete, getParameterValues],
  (dashboard, parameterValues) => {
    if (!dashboard) {
      return {};
    }
    return getParameterValuesBySlug(dashboard.parameters, parameterValues);
  },
);

export const getCanShowAutoApplyFiltersToast = createSelector(
  [
    getDashboard,
    getAutoApplyFiltersToastDashboardId,
    getIsAutoApplyFilters,
    getIsSlowDashboard,
    getIsParameterValuesEmpty,
  ],
  (
    dashboard,
    toastDashboardId,
    isAutoApply,
    isSlowDashboard,
    isParameterValuesEmpty,
  ) => {
    return (
      dashboard?.can_write &&
      dashboard?.id !== toastDashboardId &&
      isAutoApply &&
      isSlowDashboard &&
      !isParameterValuesEmpty
    );
  },
);

export const getDocumentTitle = (state: State) =>
  state.dashboard.loadingControls.documentTitle;

export const getIsNavigatingBackToDashboard = (state: State) =>
  state.dashboard.isNavigatingBackToDashboard;

export const getIsDirty = createSelector(
  [getDashboard, getDashcards],
  (dashboard, dashcards) => {
    if (!dashboard) {
      return false;
    }

    if (dashboard.isDirty) {
      return true;
    }

    return dashboard.dashcards.some((id) => {
      const dc = dashcards[id];
      return (
        !(dc.isAdded && dc.isRemoved) &&
        (dc.isDirty || dc.isAdded || dc.isRemoved)
      );
    });
  },
);

export const getEditingDashcardId = createSelector([getSidebar], (sidebar) => {
  return sidebar?.props?.dashcardId;
});

export const getEditingParameterId = createSelector([getSidebar], (sidebar) => {
  return isEditParameterSidebar(sidebar) ? sidebar.props?.parameterId : null;
});

export const getIsEditingParameter = createSelector(
  [getEditingParameterId],
  (parameterId) => parameterId != null,
);

export const getEditingParameter = createSelector(
  [getDashboard, getEditingParameterId],
  (dashboard, editingParameterId) => {
    const parameters = dashboard?.parameters || [];
    return editingParameterId != null
      ? _.findWhere(parameters, { id: editingParameterId })
      : null;
  },
);

/**
 * Returns the dashcard id of the dashcard that contains the parameter.
 * If the parameter is dashboard header parameter, it returns undefined.
 */
export const getEditingParameterInlineDashcard = createSelector(
  [getEditingParameterId, getDashcards],
  (editingParameterId, dashcards) => {
    return editingParameterId
      ? findDashCardForInlineParameter(
          editingParameterId,
          Object.values(dashcards),
        )
      : undefined;
  },
);

const getCard = (state: State, { card }: { card: Card | VirtualCard }) => card;
const getDashCard = (state: State, { dashcard }: { dashcard: DashboardCard }) =>
  dashcard;

export const getParameterTarget = createSelector(
  [getEditingParameter, getCard, getDashCard],
  (parameter, card, dashcard) => {
    if (parameter == null) {
      return null;
    }

    const parameterMappings = dashcard.parameter_mappings || [];

    const lookupProperties = card?.id
      ? { parameter_id: parameter.id, card_id: card.id }
      : { parameter_id: parameter.id };

    const mapping = _.findWhere(parameterMappings, lookupProperties);
    return mapping?.target;
  },
);

export const getQuestions = createSelector(
  [getDashboardComplete, selectQuestionFromCardBuilder],
  (dashboard, buildQuestion) => {
    if (!dashboard) {
      return {};
    }
    return getDashboardQuestions(dashboard.dashcards, buildQuestion);
  },
);

export const getParameters = createSelector(
  [
    getDashboardComplete,
    selectQuestionFromCardBuilder,
    getQuestions,
    getIsEditing,
  ],
  (dashboard, buildQuestion, questions, isEditing) => {
    if (!dashboard) {
      return [];
    }

    return isEditing
      ? getUnsavedDashboardUiParameters(
          dashboard.dashcards,
          dashboard.parameters,
          buildQuestion,
          questions,
        )
      : getSavedDashboardUiParameters(
          dashboard.dashcards,
          dashboard.parameters,
          dashboard.param_fields,
        );
  },
);

export const getDashboardHeaderParameters = createSelector(
  [getCurrentDashcards, getParameters],
  (dashcardList, parameters) => {
    return parameters.filter(
      (parameter) => !isDashcardInlineParameter(parameter.id, dashcardList),
    );
  },
);

export const getDashboardHeaderValuePopulatedParameters = createSelector(
  [getDashboardHeaderParameters, getEffectiveParameterValues],
  (parameters, values) => _getValuePopulatedParameters({ parameters, values }),
);

export const getDashCardInlineValuePopulatedParameters = createSelector(
  [
    getDashcards,
    getParameters,
    getEffectiveParameterValues,
    (_, dashcardId: number) => dashcardId,
  ],
  (dashcards, parameters, parameterValues, dashcardId) => {
    const dashcard = dashcards[dashcardId];
    if (!dashcard || !hasInlineParameters(dashcard)) {
      return [];
    }
    const inlineParameters = dashcard.inline_parameters
      .map((parameterId) => parameters.find((p) => p.id === parameterId))
      .filter(isNotNull);
    return _getValuePopulatedParameters({
      parameters: inlineParameters,
      values: parameterValues,
    });
  },
);

export const getValuePopulatedParameters = createSelector(
  [getParameters, getEffectiveParameterValues],
  (parameters, values) => _getValuePopulatedParameters({ parameters, values }),
);

export const getMissingRequiredParameters = createSelector(
  [getParameters],
  (parameters) =>
    parameters.filter(
      (p) =>
        p.required &&
        (!p.default || (Array.isArray(p.default) && p.default.length === 0)),
    ),
);

/**
 * Holds one Question per card, so that connected components keep the same
 * reference between renders. reselect keys weakly on the card and the metadata,
 * so an entry is released once its card leaves the store or the metadata is
 * replaced. Keying on the card id instead would hold every Question, and the
 * metadata snapshot each one carries, for the life of the tab.
 */
export const getQuestionByCard = createSelector(
  [
    (_state: State, props: { card: Card | VirtualCard }) => props.card,
    selectQuestionFromCardBuilder,
  ],
  (card, buildQuestion) => {
    return isQuestionCard(card) ? buildQuestion(card) : undefined;
  },
);

export const getDashcardParameterMappingOptions = createSelector(
  [getQuestionByCard, getEditingParameter, getCard, getDashCard, getDashcards],
  (question, parameter, card, dashcard, dashcards) => {
    const parameterDashcard =
      parameter != null
        ? findDashCardForInlineParameter(parameter.id, Object.values(dashcards))
        : null;
    return _getParameterMappingOptions(
      question,
      parameter,
      card,
      dashcard,
      parameterDashcard,
    );
  },
);

// Embeddings might be published without passing embedding_params to the server,
// in which case it's an empty object. We should treat such situations with
// caution, assuming that an absent parameter is "disabled".
export function getEmbeddedParameterVisibility(
  state: State,
  slug: string,
): EmbeddingParameterVisibility | null {
  const dashboard = getDashboard(state);
  if (!dashboard?.enable_embedding) {
    return null;
  }

  const embeddingParams = dashboard.embedding_params ?? {};
  return embeddingParams[slug] ?? "disabled";
}

export const getIsHeaderVisible = createSelector(
  [selectIsWithinIframe, getEmbedOptions],
  (isEmbeddingIframe, embedOptions) =>
    (isEmbeddingSdk() && isEmbeddingIframe) ||
    !isEmbeddingIframe ||
    !!embedOptions.header,
);

export const getIsAdditionalInfoVisible = createSelector(
  [selectIsWithinIframe, getEmbedOptions],
  (isEmbeddingIframe, embedOptions) =>
    !isEmbeddingIframe || !!embedOptions.additional_info,
);

export const getTabs = createSelector([getDashboard], (dashboard) => {
  if (!dashboard) {
    return [];
  }
  return dashboard.tabs?.filter((tab) => !tab.isRemoved) ?? [];
});

export const getSelectedTabId = createSelector(
  [
    getIsWebApp,
    (state) => getSetting(state, "site-url"),
    getDashboard,
    (state) => state.dashboard.selectedTabId,
    (state: State & Partial<SdkSharedStoreState>) =>
      state.sdk?.initialDashboardTabId,
  ],
  (isWebApp, siteUrl, dashboard, selectedTabId, sdkInitialDashboardTabId) => {
    if (dashboard && selectedTabId === null) {
      if (isEmbeddingSdk()) {
        return getSdkInitialDashboardTabId(dashboard, sdkInitialDashboardTabId);
      } else {
        return getInitialSelectedTabId(dashboard, siteUrl, isWebApp);
      }
    } else {
      return selectedTabId;
    }
  },
);

export const getSelectedTab = createSelector(
  [getDashboard, getSelectedTabId],
  (dashboard, selectedTabId) => {
    if (!dashboard || selectedTabId === null) {
      return null;
    }
    return dashboard.tabs?.find((tab) => tab.id === selectedTabId) || null;
  },
);

function getInitialSelectedTabId(
  dashboard: Dashboard | StoreDashboard,
  siteUrl: string,
  isWebApp: boolean,
) {
  const pathname = getPathnameWithoutSubPath(window.location.pathname, siteUrl);
  const isDashboardUrl = pathname.includes("/dashboard/");

  if (isDashboardUrl) {
    const dashboardSlug = pathname.replace("/dashboard/", "");
    const dashboardUrlId = Urls.extractEntityId(dashboardSlug);
    const isNavigationInProgress = dashboardUrlId !== dashboard.id;
    if (!isNavigationInProgress || !isWebApp) {
      const searchParams = new URLSearchParams(window.location.search);
      const tabParam = searchParams.get("tab");
      const tabId = tabParam ? parseInt(tabParam, 10) : null;
      const hasTab = dashboard.tabs?.some?.((tab) => tab.id === tabId);
      if (hasTab) {
        return tabId;
      }
    }
  }

  return dashboard.tabs?.[0]?.id || null;
}

function getSdkInitialDashboardTabId(
  dashboard: Dashboard | StoreDashboard,
  sdkInitialDashboardTabId: DashboardTabId | null | undefined,
) {
  const hasTab = dashboard.tabs?.some(
    (tab) => tab.id === sdkInitialDashboardTabId,
  );
  if (hasTab) {
    return sdkInitialDashboardTabId ?? null;
  }
  return dashboard.tabs?.[0]?.id ?? null;
}

export const getHiddenParameterSlugs = createSelector(
  [getDashboardComplete, getParameters, getIsEditing],
  (dashboard, parameters, isEditing) => {
    if (isEditing || !dashboard) {
      // All filters should be visible in edit mode
      return undefined;
    }

    const parameterIds = getMappedParametersIds(dashboard.dashcards);
    const hiddenParameters = parameters.filter(
      (parameter) => !parameterIds.includes(parameter.id),
    );

    return hiddenParameters.map((parameter) => parameter.slug).join(",");
  },
);

export const getParameterMappingsBeforeEditing = createSelector(
  [getDashboardBeforeEditing],
  (editingDashboard) => {
    if (!editingDashboard) {
      return {};
    }

    const dashcards = editingDashboard.dashcards;
    const map: Record<
      ParameterId,
      Record<DashCardId, DashboardParameterMapping>
    > = {};

    // create a map like {[parameterId]: {[dashcardId]: parameterMapping}}
    for (const dashcard of dashcards) {
      if (!dashcard.parameter_mappings) {
        continue;
      }

      for (const parameterMapping of dashcard.parameter_mappings) {
        const parameterId = parameterMapping.parameter_id;

        if (!map[parameterId]) {
          map[parameterId] = {};
        }

        map[parameterId][dashcard.id] =
          // Unjustified type cast. FIXME
          parameterMapping as DashboardParameterMapping;
      }
    }

    return map;
  },
);

export const getHasModelActionsEnabled = createSelector(
  [getShallowDatabases],
  (databases) => {
    const hasModelActionsEnabled = Object.values(databases).some((database) =>
      // @ts-expect-error Schema types do not match
      hasDatabaseActionsEnabled(database),
    );

    return hasModelActionsEnabled;
  },
);

export const getFiltersToReset = createSelector(
  [getValuePopulatedParameters, getHiddenParameterSlugs],
  (parameters, hiddenParameterSlugs) => {
    const visibleParameters = getVisibleParameters(
      parameters,
      hiddenParameterSlugs,
    );
    return visibleParameters.filter(canResetFilter);
  },
);

export const getCanResetFilters = createSelector(
  [getFiltersToReset],
  (filtersToReset) => filtersToReset.length > 0,
);

// Defined in a leaf module so the app shell can read them without pulling this
// one in. Re-exported so every existing call site is unchanged.
export {
  getDashboard,
  getDashboardBeforeEditing,
  getDashboardId,
  getDashboards,
  getIsEditing,
};
